/**
 * GlassSelect — reusable frosted-glass dropdown (v7.5.15).
 *
 * Replaces native <select> elements across the app. Solves two problems
 * in one component:
 *   1. Chromium on Windows ignores `color-scheme: dark` for native <select>
 *      popups (the popup chrome is rendered by Win32, not by the renderer).
 *      Native popups stay light no matter how we style them.
 *   2. Native popups can't have search, can't be styled, can't show icons
 *      or grouping, and have inconsistent keyboard behavior across browsers.
 *
 * Visual style matches the Journal day cards:
 *   - Trigger uses the existing `.input-modern` class so it slots into any
 *     form layout without re-styling.
 *   - Popup: `rounded-xl bg-gray-950/85 backdrop-blur-xl border-white/10`
 *     in dark mode; `rounded-xl bg-white/95 backdrop-blur-xl border-gray-200`
 *     in light mode. Soft `shadow-xl`. Same frost language as the JournifiSync
 *     modal but lighter (rounded-xl vs rounded-2xl, less padding).
 *
 * Features:
 *   - Search box auto-shows when options.length > 8 (overridable via `searchable`).
 *   - Keyboard nav: ↑↓ to move cursor, Enter to select, Esc to close,
 *     typing focuses search (when searchable).
 *   - Click outside to close.
 *   - Selected option marked with a ✓ checkmark.
 *   - Popup flips above the trigger when there's no room below.
 *   - Accepts string[] or {value, label, group?, disabled?}[] options.
 *
 * Pure dropdown — no multi-select. (Future MultiGlassSelect can compose
 * this when needed.)
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, Check, Search, X } from 'lucide-react';

/**
 * Normalize options to a consistent shape internally.
 * Accepts: string[] | {value, label, group?, disabled?}[]
 * Returns: {value, label, group?, disabled?}[]
 */
const normalizeOptions = (options) => {
  if (!Array.isArray(options)) return [];
  return options.map(o => {
    if (typeof o === 'string') return { value: o, label: o };
    if (o && typeof o === 'object') {
      return {
        value: o.value,
        label: o.label ?? String(o.value ?? ''),
        group: o.group,
        disabled: !!o.disabled,
      };
    }
    return { value: o, label: String(o) };
  });
};

const SEARCH_THRESHOLD = 8;

const GlassSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  searchable,             // undefined = auto (>8 options); true/false = force
  disabled = false,
  darkMode = true,
  className = '',         // applied to default trigger button
  popupClassName = '',    // applied to popup
  wrapperClassName,       // optional — overrides the default 'relative inline-block w-full'
                          // wrapper class. Pass 'relative inline-block' when the parent
                          // shouldn't be full-width (e.g., the SlicerBar RangeChip pill).
  ariaLabel,
  size = 'md',            // 'sm' | 'md' (controls trigger padding)
  emptyText = 'No matches',
  renderTrigger,          // optional (triggerProps, triggerState) => JSX. When provided,
                          // fully replaces the default trigger button. The caller spreads
                          // `triggerProps` onto their root element to wire up ref + click +
                          // keyboard + aria. `triggerState` contains { value, isOpen,
                          // selectedOption, placeholder, disabled } for conditional styling.
                          // Used by SlicerBar's RangeChip to preserve its pill shape +
                          // "Range: X" trigger-label trick.
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursorIdx, setCursorIdx] = useState(-1);
  const [popupAbove, setPopupAbove] = useState(false);

  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionRefs = useRef([]);

  const normalized = useMemo(() => normalizeOptions(options), [options]);
  const showSearch = searchable ?? normalized.length > SEARCH_THRESHOLD;

  // Filter options by search query (case-insensitive substring on label)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter(o => String(o.label).toLowerCase().includes(q));
  }, [normalized, query]);

  // Find the option matching the current value (for the trigger label)
  const selectedOption = useMemo(() => {
    return normalized.find(o => o.value === value) || null;
  }, [normalized, value]);

  // ── open/close behavior ────────────────────────────────────────────────
  const openPopup = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setQuery('');
    // Cursor starts on currently-selected option (or first if none selected)
    const idx = normalized.findIndex(o => o.value === value);
    setCursorIdx(idx >= 0 ? idx : 0);
    // Decide whether the popup needs to flip above the trigger
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Need ~280px for a comfortable popup; flip up if below is tight AND
      // above has more room.
      setPopupAbove(spaceBelow < 280 && spaceAbove > spaceBelow);
    }
  }, [disabled, normalized, value]);

  const closePopup = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setCursorIdx(-1);
  }, []);

  const selectValue = useCallback((v) => {
    if (onChange) onChange(v);
    closePopup();
    // Return focus to the trigger after selection for keyboard flow.
    triggerRef.current?.focus();
  }, [onChange, closePopup]);

  // Click-outside to close
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (popupRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      closePopup();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, closePopup]);

  // Auto-focus the search input when opened (if searchable)
  useEffect(() => {
    if (isOpen && showSearch) {
      // Defer to next tick so the input is mounted
      const t = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isOpen, showSearch]);

  // Keep cursor option scrolled into view while keyboard-navigating
  useEffect(() => {
    if (!isOpen || cursorIdx < 0) return;
    const el = optionRefs.current[cursorIdx];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [cursorIdx, isOpen]);

  // Reset cursor to first match whenever the filtered list changes
  useEffect(() => {
    if (!isOpen) return;
    setCursorIdx(filtered.length > 0 ? 0 : -1);
  }, [filtered, isOpen]);

  // ── keyboard handling ──────────────────────────────────────────────────
  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      openPopup();
    }
  };

  const handlePopupKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closePopup();
      triggerRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursorIdx(idx => {
        const next = idx + 1;
        return next >= filtered.length ? 0 : next;
      });
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursorIdx(idx => {
        const prev = idx - 1;
        return prev < 0 ? filtered.length - 1 : prev;
      });
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[cursorIdx];
      if (opt && !opt.disabled) selectValue(opt.value);
    }
  };

  // ── styling helpers ────────────────────────────────────────────────────
  // We re-use .input-modern for the trigger so it matches every other form
  // control in the app automatically. The trigger is a <button>, not a div,
  // so it gets native keyboard + focus behavior for free.
  const sizeClass = size === 'sm' ? 'py-1 px-2 text-xs' : '';
  const triggerClasses = [
    'w-full input-modern flex items-center justify-between text-left',
    sizeClass,
    disabled ? 'opacity-55 cursor-not-allowed' : 'cursor-pointer',
    className,
  ].filter(Boolean).join(' ');

  const popupClasses = [
    'absolute z-50 left-0 right-0 min-w-full',
    popupAbove ? 'bottom-full mb-1' : 'top-full mt-1',
    'rounded-xl border shadow-xl overflow-hidden',
    darkMode
      ? 'bg-gray-950/85 backdrop-blur-xl border-white/10'
      : 'bg-white/95 backdrop-blur-xl border-gray-200',
    popupClassName,
  ].filter(Boolean).join(' ');

  const optionClass = (idx, opt) => {
    const isSelected = opt.value === value;
    const isCursor = idx === cursorIdx;
    const base = 'flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors duration-100';
    const disabledClass = opt.disabled ? 'opacity-40 cursor-not-allowed' : '';
    if (darkMode) {
      const bg = isCursor
        ? 'bg-white/[0.08]'
        : isSelected
          ? 'bg-purple-500/15'
          : '';
      const text = isSelected ? 'text-purple-100' : 'text-gray-200';
      return [base, bg, text, disabledClass].filter(Boolean).join(' ');
    }
    const bg = isCursor
      ? 'bg-gray-100'
      : isSelected
        ? 'bg-purple-100'
        : '';
    const text = isSelected ? 'text-purple-900' : 'text-gray-800';
    return [base, bg, text, disabledClass].filter(Boolean).join(' ');
  };

  // Shared trigger handlers — spread onto the default trigger OR onto the
  // caller's renderTrigger root. This is the "headless UI" pattern: the
  // component owns the behavior, the caller owns the markup.
  const triggerProps = {
    ref: triggerRef,
    onClick: () => (isOpen ? closePopup() : openPopup()),
    onKeyDown: handleTriggerKeyDown,
    disabled,
    'aria-haspopup': 'listbox',
    'aria-expanded': isOpen,
    'aria-label': ariaLabel,
  };
  const triggerState = { value, isOpen, selectedOption, placeholder, disabled };

  // ── render ────────────────────────────────────────────────────────────
  const wrapperClass = wrapperClassName ?? 'relative inline-block w-full';
  return (
    <div className={wrapperClass}>
      {/* Trigger — either custom via renderTrigger, or the default button */}
      {renderTrigger ? (
        renderTrigger(triggerProps, triggerState)
      ) : (
        <button
          type="button"
          {...triggerProps}
          className={triggerClasses}
        >
          <span className={`truncate ${selectedOption ? '' : (darkMode ? 'text-gray-500' : 'text-gray-400')}`}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown
            size={14}
            className={`shrink-0 ml-1 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
          />
        </button>
      )}

      {/* Popup */}
      {isOpen && (
        <div
          ref={popupRef}
          role="listbox"
          aria-label={ariaLabel}
          onKeyDown={handlePopupKeyDown}
          tabIndex={-1}
          className={popupClasses}
        >
          {/* Search input (auto-shown if >8 options or explicitly enabled) */}
          {showSearch && (
            <div className={`flex items-center gap-2 px-2.5 py-2 border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
              <Search size={14} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className={`flex-1 bg-transparent text-sm outline-none ${darkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className={darkMode ? 'text-gray-400' : 'text-gray-500'}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )}

          {/* Option list */}
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className={`px-3 py-3 text-sm italic text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {emptyText}
              </div>
            ) : (
              filtered.map((opt, idx) => (
                <div
                  key={String(opt.value) + ':' + idx}
                  ref={(el) => { optionRefs.current[idx] = el; }}
                  role="option"
                  aria-selected={opt.value === value}
                  onClick={() => !opt.disabled && selectValue(opt.value)}
                  onMouseEnter={() => setCursorIdx(idx)}
                  className={optionClass(idx, opt)}
                >
                  <span className="flex-1 truncate">{opt.label}</span>
                  {opt.value === value && (
                    <Check
                      size={14}
                      className={darkMode ? 'text-purple-300 shrink-0' : 'text-purple-600 shrink-0'}
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlassSelect;
